import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { UniversalUpload } from './UniversalUpload';
import { ArquivosMoto } from './ArquivosMoto';
import { SearchBar } from './SearchBar';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { ModernPageHeader, ModernStatCard } from '../ui/ModernComponents';
import { 
  Plus, 
  Edit, 
  AlertTriangle, 
  Car, 
  Gauge,
  Calendar,
  Wrench,
  Trash2,
  Folder,
  Eye,
  Camera,
  Bike,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { apiService } from '../../services/api';

export const MotosPage = () => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isSubAdmin = user?.tipo === 'sub_admin';

  const [motos, setMotos] = useState([]);
  const [busca, setBusca] = useState('');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingMoto, setEditingMoto] = useState(null);
  const [showArquivos, setShowArquivos] = useState(false);
  const [motoSelecionada, setMotoSelecionada] = useState(null);
  const [showUploadFoto, setShowUploadFoto] = useState(false);
  const [motoParaFoto, setMotoParaFoto] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    placa: '',
    cor: '',
    ano: new Date().getFullYear(),
    km_atual: 0,
    data_aquisicao: new Date().toISOString().split('T')[0],
    observacoes: ''
  });

  useEffect(() => {
    carregarMotos();
  }, []);

  const carregarMotos = async () => {
    try {
      const data = await apiService.getMotos();
      
      // Buscar foto de perfil para cada moto
      const motosComFotos = await Promise.all(
        data.map(async (moto) => {
          try {
            // Buscar arquivos da moto
            const arquivos = await apiService.getArquivosMoto(moto.id);
            
            // Encontrar a foto marcada como perfil (apenas arquivos de imagem)
            const fotoPerfil = arquivos.find(arquivo => {
              const isProfile = arquivo.is_profile;
              const isImage = (arquivo.tipo?.toLowerCase() === 'foto' ||
                             arquivo.content_type?.toLowerCase().startsWith('image/') ||
                             arquivo.nome_original?.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp|bmp)$/)) &&
                             !arquivo.nome_original?.toLowerCase().match(/\.(txt|doc|pdf|docx)$/);
              return isProfile && isImage;
            });
            
            return {
              ...moto,
              foto_perfil: fotoPerfil ? `${process.env.REACT_APP_BACKEND_URL}/api/arquivos/${fotoPerfil.id}` : null
            };
          } catch (error) {
            console.error(`Erro ao carregar foto da moto ${moto.id}:`, error);
            return {
              ...moto,
              foto_perfil: null
            };
          }
        })
      );
      
      setMotos(motosComFotos);
    } catch (error) {
      console.error('Erro ao carregar motos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Validação específica para mudança de placa
      if (editingMoto && editingMoto.placa !== formData.placa) {
        const confirmacao = window.confirm(
          `⚠️ ALTERAÇÃO DE PLACA DETECTADA\n\nPlaca atual: ${editingMoto.placa}\nNova placa: ${formData.placa}\n\nTem certeza que deseja alterar a placa desta moto?\n\nISTO PODE AFETAR LOCAÇÕES E HISTÓRICOS EXISTENTES.`
        );
        if (!confirmacao) {
          return;
        }
      }
      
      if (editingMoto) {
        await apiService.atualizarMoto(editingMoto.id, {
          ...formData,
          modelo: 'DK 160' // Modelo padrão
        });
        alert(`✅ Moto atualizada com sucesso!${editingMoto.placa !== formData.placa ? '\n\n📝 Placa alterada de ' + editingMoto.placa + ' para ' + formData.placa : ''}`);
      } else {
        await apiService.criarMoto({
          ...formData,
          modelo: 'DK 160' // Modelo padrão
        });
        alert('Moto cadastrada com sucesso!');
      }
      
      setShowModal(false);
      setEditingMoto(null);
      resetForm();
      await carregarMotos();
    } catch (error) {
      console.error('Erro ao salvar moto:', error);
      alert('Erro ao salvar moto: ' + (error.response?.data?.detail || error.message));
    }
  };

  const resetForm = () => {
    setFormData({
      placa: '',
      cor: '',
      ano: new Date().getFullYear(),
      km_atual: 0,
      data_aquisicao: new Date().toISOString().split('T')[0],
      observacoes: ''
    });
  };

  const handleEdit = (moto) => {
    setEditingMoto(moto);
    setFormData({
      placa: moto.placa,
      cor: moto.cor,
      ano: moto.ano,
      km_atual: moto.km_atual,
      data_aquisicao: moto.data_aquisicao,
      observacoes: moto.observacoes || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (moto) => {
    if (window.confirm(`Tem certeza que deseja excluir a moto ${moto.placa}?`)) {
      try {
        await apiService.deletarMoto(moto.id);
        carregarMotos();
        alert('Moto excluída com sucesso!');
      } catch (error) {
        console.error('Erro ao excluir moto:', error);
        alert('Erro ao excluir moto. Tente novamente.');
      }
    }
  };

  const handleVerArquivos = (moto) => {
    setMotoSelecionada(moto);
    setShowArquivos(true);
  };

  const handleClickFotoPerfil = (moto) => {
    setMotoParaFoto(moto);
    setShowUploadFoto(true);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUploadFotoPerfil = async () => {
    if (!selectedFile || !motoParaFoto) {
      alert('Selecione um arquivo primeiro.');
      return;
    }
    
    console.log('📤 Iniciando upload:', {
      arquivo: selectedFile.name,
      tamanho: selectedFile.size,
      tipo: selectedFile.type,
      moto: motoParaFoto.placa
    });
    
    setUploading(true);
    try {
      // Fazer upload do arquivo
      console.log('🔄 Fazendo upload do arquivo...');
      const resultado = await apiService.uploadArquivoMoto(motoParaFoto.id, selectedFile, 'Foto', 'Foto de perfil da moto');
      console.log('✅ Upload realizado:', resultado);
      
      // Marcar como foto de perfil
      console.log('🏷️ Definindo como foto de perfil...');
      await apiService.atualizarArquivoMoto(resultado.arquivo_id, { is_profile: true });
      console.log('✅ Foto definida como perfil');
      
      // Recarregar motos para mostrar a nova foto
      console.log('🔄 Recarregando lista de motos...');
      await carregarMotos();
      
      // Fechar modal e limpar estados
      setShowUploadFoto(false);
      setMotoParaFoto(null);
      setSelectedFile(null);
      
      alert('Foto de perfil definida com sucesso!');
      
    } catch (error) {
      console.error('❌ Erro detalhado ao fazer upload da foto:', error);
      console.error('❌ Resposta do erro:', error.response?.data);
      console.error('❌ Status do erro:', error.response?.status);
      
      let mensagem = 'Erro ao enviar foto. ';
      if (error.response?.data?.detail) {
        mensagem += error.response.data.detail;
      } else if (error.message) {
        mensagem += error.message;
      }
      
      alert(mensagem + ' Tente novamente.');
    } finally {
      setUploading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'disponivel': return 'success';
      case 'alugada': return 'info';
      case 'manutencao': return 'warning';
      case 'inativa': return 'default';
      default: return 'default';
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'disponivel': return 'bg-green-500/20 text-green-400 border border-green-500/30';
      case 'alugada': return 'bg-blue-500/20 text-blue-400 border border-blue-500/30';
      case 'manutencao': return 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30';
      case 'inativa': return 'bg-gray-500/20 text-gray-400 border border-gray-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border border-gray-500/30';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'disponivel': return 'Disponível';
      case 'alugada': return 'Alugada';
      case 'manutencao': return 'Manutenção';
      case 'inativa': return 'Inativa';
      default: return status;
    }
  };

  const precisaManutencao = (moto) => {
    const kmDesdeManutencao = moto.km_atual - (moto.km_ultima_manutencao || 0);
    return kmDesdeManutencao >= 4500;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-green-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-400">Carregando motos...</p>
        </div>
      </div>
    );
  }

  // Calcular estatísticas
  const totalMotos = motos.length;
  const motosAlugadas = motos.filter(m => m.status === 'alugada').length;
  const motosDisponiveis = motos.filter(m => m.status === 'disponivel').length;
  const motosManutencao = motos.filter(m => m.status === 'manutencao').length;

  const qMoto = busca.trim().toLowerCase();
  const motosFiltradas = qMoto ? motos.filter(m =>
    (m.placa || '').toLowerCase().includes(qMoto) ||
    (m.modelo || '').toLowerCase().includes(qMoto) ||
    (m.cor || '').toLowerCase().includes(qMoto) ||
    (m.status || '').toLowerCase().includes(qMoto)
  ) : motos;

  return (
    <div className="space-y-6 min-h-screen pb-20" data-testid="motos-page">
      {/* Header Moderno */}
      <ModernPageHeader
        title="Gestão de Motos"
        subtitle={`${totalMotos} motos cadastradas`}
        icon={Car}
        gradient="from-blue-900 to-slate-900"
        actions={
          <Dialog open={showModal} onOpenChange={(v) => !isSubAdmin && setShowModal(v)}>
            <DialogTrigger asChild>
              <Button 
                data-testid="btn-nova-moto"
                disabled={isSubAdmin}
                className={`bg-gradient-to-r from-green-600 to-green-700 hover:from-green-500 hover:to-green-600 text-white rounded-xl px-6 ${isSubAdmin ? 'opacity-50 cursor-not-allowed' : ''}`}
                onClick={() => {
                  if (isSubAdmin) return;
                  setEditingMoto(null);
                  resetForm();
                }}
              >
                <Plus className="h-4 w-4 mr-2" />
                Nova Moto
              </Button>
            </DialogTrigger>
            
            <DialogContent className="max-w-md bg-slate-800 border-white/10">
              <DialogHeader>
                <DialogTitle className="text-white">
                  {editingMoto ? `Editar Moto ${editingMoto.placa}` : 'Nova Moto'}
                </DialogTitle>
              </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Campo Placa - Sempre visível, mas com destaque na edição */}
              <div>
                <Label htmlFor="placa">Placa</Label>
                <Input
                  id="placa"
                  value={formData.placa}
                  onChange={(e) => setFormData({...formData, placa: e.target.value.toUpperCase()})}
                  placeholder="ABC-1234 ou UEG-0A20"
                  maxLength={8}
                  required
                  className={editingMoto ? "border-yellow-300 bg-yellow-50" : ""}
                />
                {editingMoto && (
                  <p className="text-xs text-yellow-600 mt-1">
                    ⚠️ Alteração de placa: Verifique se a nova placa está correta antes de salvar. Isso atualizará todas as locações.
                  </p>
                )}
              </div>
              
              {!editingMoto && (
                <>
                  
                  <div>
                    <Label htmlFor="cor">Cor</Label>
                    <Select value={formData.cor} onValueChange={(value) => setFormData({...formData, cor: value})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione a cor" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Vermelha">Vermelha</SelectItem>
                        <SelectItem value="Azul">Azul</SelectItem>
                        <SelectItem value="Preta">Preta</SelectItem>
                        <SelectItem value="Branca">Branca</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label htmlFor="ano">Ano</Label>
                    <Input
                      id="ano"
                      type="number"
                      value={formData.ano}
                      onChange={(e) => setFormData({...formData, ano: e.target.value})}
                      min="2020"
                      max={new Date().getFullYear()}
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="data_aquisicao">Data de Aquisição</Label>
                    <Input
                      id="data_aquisicao"
                      type="date"
                      value={formData.data_aquisicao}
                      onChange={(e) => setFormData({...formData, data_aquisicao: e.target.value})}
                      required
                    />
                  </div>
                </>
              )}
              
              <div>
                <Label htmlFor="km_atual">Quilometragem Atual</Label>
                <Input
                  id="km_atual"
                  type="number"
                  value={formData.km_atual}
                  onChange={(e) => setFormData({...formData, km_atual: e.target.value})}
                  min="0"
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="observacoes">Observações</Label>
                <Textarea
                  id="observacoes"
                  value={formData.observacoes}
                  onChange={(e) => setFormData({...formData, observacoes: e.target.value})}
                  placeholder="Observações sobre a moto..."
                />
              </div>
              
              {/* Upload de Fotos e Documentos */}
              {editingMoto?.id && (
                <div className="mt-6">
                  <h3 className="text-lg font-medium mb-4">Fotos e Documentos</h3>
                  <UniversalUpload 
                    entityId={editingMoto.id}
                    entityType="moto"
                    allowedTypes={['Foto', 'Documento', 'Manual', 'Nota Fiscal']}
                    showProfilePicture={true}
                    onProfilePictureSet={(url) => console.log('Foto principal definida:', url)}
                  />
                </div>
              )}
              
              <div className="flex space-x-2 pt-4">
                <Button type="submit" className="flex-1 bg-green-600 hover:bg-green-700">
                  {editingMoto ? 'Salvar Alterações' : 'Cadastrar Moto'}
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowModal(false)}
                  className="flex-1"
                >
                  Cancelar
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      }
      />

      {/* Cards de Estatísticas */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <ModernStatCard
          icon={Car}
          value={totalMotos}
          label="Total de Motos"
          gradient="from-blue-600 to-blue-800"
        />
        <ModernStatCard
          icon={Bike}
          value={motosAlugadas}
          label="Alugadas"
          gradient="from-green-600 to-emerald-800"
        />
        <ModernStatCard
          icon={CheckCircle}
          value={motosDisponiveis}
          label="Disponíveis"
          gradient="from-cyan-600 to-cyan-800"
        />
        <ModernStatCard
          icon={Wrench}
          value={motosManutencao}
          label="Em Manutenção"
          gradient="from-yellow-600 to-orange-700"
        />
      </div>

      {/* 🔎 Busca */}
      <SearchBar
        value={busca}
        onChange={setBusca}
        placeholder="Buscar por placa, modelo, cor ou status..."
        testId="motos-busca-input"
        resultado={busca ? motosFiltradas.length : undefined}
      />

      {/* Lista de Motos */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {motosFiltradas.map((moto) => (
          <div 
            key={moto.id} 
            className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-700/50 to-slate-800/50 border border-white/5 backdrop-blur-sm transition-all duration-300 hover:border-white/10 hover:shadow-xl"
          >
            <div className="p-5">
              {/* Header do Card */}
              <div className="flex items-start gap-4">
                {/* Foto de Perfil */}
                <div 
                  className="w-20 h-20 rounded-xl overflow-hidden flex-shrink-0 bg-slate-700 cursor-pointer hover:ring-2 hover:ring-green-500 transition-all relative group/foto"
                  onClick={() => handleClickFotoPerfil(moto)}
                  title="Clique para alterar foto"
                >
                  {moto.foto_perfil ? (
                    <>
                      <img 
                        src={moto.foto_perfil} 
                        alt={`${moto.placa}`}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover/foto:opacity-100 flex items-center justify-center transition-opacity">
                        <Camera className="h-5 w-5 text-white" />
                      </div>
                    </>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-600 to-slate-700">
                      <div className="text-center">
                        <Camera className="h-6 w-6 text-gray-400 mx-auto" />
                        <span className="text-xs text-gray-400">Foto</span>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-semibold text-white text-lg">DK 160 - {moto.cor}</h3>
                      <p className="text-sm text-gray-400 font-mono">{moto.placa}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(moto.status)}`}>
                      {getStatusText(moto.status)}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Detalhes */}
              <div className="mt-4 grid grid-cols-2 gap-3">
                <div className="flex items-center gap-2 text-sm text-gray-300">
                  <Calendar className="h-4 w-4 text-gray-500" />
                  <span>{moto.ano}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-300">
                  <Gauge className="h-4 w-4 text-gray-500" />
                  <span>{moto.km_atual ? moto.km_atual.toLocaleString() : '0'} km</span>
                </div>
              </div>
              
              {/* Info de Manutenção */}
              {moto.km_ultima_manutencao > 0 && (
                <div className="mt-3 p-3 rounded-xl bg-white/5 border border-white/5">
                  <div className="flex items-center gap-2 text-xs text-gray-400">
                    <Wrench className="h-3.5 w-3.5" />
                    <span>Última manutenção: {moto.km_ultima_manutencao?.toLocaleString() || 'N/A'} km</span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {(moto.km_atual - (moto.km_ultima_manutencao || 0)).toLocaleString()} km rodados desde
                  </div>
                </div>
              )}
              
              {/* Observações */}
              {moto.observacoes && (
                <div className="mt-3 p-2 rounded-lg bg-blue-500/10 border border-blue-500/20">
                  <p className="text-xs text-blue-300">{moto.observacoes}</p>
                </div>
              )}
              
              {/* Ações */}
              <div className="mt-4 pt-4 border-t border-white/5 grid grid-cols-2 gap-2">
                {!isSubAdmin && (
                  <Button 
                    data-testid={`btn-editar-moto-${moto.id}`}
                    variant="outline" 
                    size="sm" 
                    className="bg-white/5 border-white/10 text-gray-300 hover:bg-white/10 hover:text-white rounded-xl"
                    onClick={() => handleEdit(moto)}
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Editar
                  </Button>
                )}
                <Button 
                  variant="outline" 
                  size="sm" 
                  className={`bg-white/5 border-white/10 text-gray-300 hover:bg-white/10 hover:text-white rounded-xl ${isSubAdmin ? 'col-span-2' : ''}`}
                  onClick={() => handleVerArquivos(moto)}
                >
                  <Folder className="h-4 w-4 mr-2" />
                  Arquivos
                </Button>
              </div>
              {!isSubAdmin && (
                <Button 
                  data-testid={`btn-excluir-moto-${moto.id}`}
                  variant="outline" 
                  size="sm" 
                  className="w-full mt-2 bg-red-500/10 border-red-500/20 text-red-400 hover:bg-red-500/20 hover:text-red-300 rounded-xl"
                  onClick={() => handleDelete(moto)}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Excluir
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
      
      {motos.length === 0 && (
        <div className="text-center py-16 rounded-2xl bg-gradient-to-br from-slate-800/50 to-slate-900/50 border border-white/5">
          <Car className="h-16 w-16 text-gray-600 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">
            Nenhuma moto cadastrada
          </h3>
          <p className="text-gray-400 mb-6">
            Comece cadastrando sua primeira moto DK 160
          </p>
          <Button 
            className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-500 hover:to-green-600 text-white rounded-xl px-6"
            onClick={() => setShowModal(true)}
          >
            <Plus className="h-4 w-4 mr-2" />
            Cadastrar Primeira Moto
          </Button>
        </div>
      )}

      {/* Modal de Arquivos da Moto */}
      <ArquivosMoto 
        moto={motoSelecionada}
        isOpen={showArquivos}
        onClose={() => setShowArquivos(false)}
        onArquivoUpdated={() => {
          // Recarregar motos quando arquivos forem atualizados
          carregarMotos();
        }}
      />

      {/* Modal de Upload de Foto de Perfil */}
      <Dialog open={showUploadFoto} onOpenChange={setShowUploadFoto}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <Camera className="h-5 w-5 text-emerald-400" />
              <span>Foto de Perfil</span>
            </DialogTitle>
            <p className="text-sm text-gray-400 mt-2">
              {motoParaFoto ? `${motoParaFoto.placa} - DK 160 ${motoParaFoto.cor}` : ''}
            </p>
          </DialogHeader>
          
          <div className="space-y-4">
            {motoParaFoto && (
              <>
                <div className="border-2 border-dashed border-green-200 rounded-lg p-6 text-center hover:border-green-300 transition-colors">
                  <Camera className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                  <p className="text-sm text-gray-400 mb-4">
                    Selecione uma imagem para a foto de perfil da moto
                  </p>
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="mb-4"
                  />
                  {selectedFile && (
                    <div className="mt-4">
                      <p className="text-sm font-medium">{selectedFile.name}</p>
                      <Button 
                        onClick={handleUploadFotoPerfil}
                        disabled={uploading}
                        className="mt-2"
                      >
                        {uploading ? 'Enviando...' : 'Definir como Foto de Perfil'}
                      </Button>
                    </div>
                  )}
                </div>
              </>
            )}
            
            <div className="flex justify-end space-x-2">
              <Button 
                variant="outline" 
                onClick={() => setShowUploadFoto(false)}
              >
                Cancelar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};